import React, { useState, useEffect, useRef } from "react";
import { string, arrayOf, shape, bool } from "prop-types";
import cx from "classnames";

import useClickAway from "../../common/useClickAway.js";
import { initScrolledIntoView } from "../../common/scrollIntoView.js";

import Icon from "../../components/Icon/Icon.jsx";
import ImageElement from "../../components/ImageElement/ImageElement.jsx";
import MapHeading from "./MapHeading/MapHeading.jsx";
import MultiMapChildApp, {
  propTypes as ChildMapProps,
} from "./MultiMapChildApp.jsx";
import ChildMapDialog from "./ChildMapDialog/ChildMapDialog.jsx";
import TransitionWrapper from "./TransitionWrapper.jsx";

const propTypes = {
  pageEditor: bool,
  dictionary: shape({
    worldMapLabel: string,
    exploreLabel: string,
  }),
  parentMapItem: shape({
    title: string,
    description: string,
    background: shape({
      srcSet: string,
      alt: string,
    }),
    stat: shape({
      value: string,
      description: string,
    }),
    childMapItems: arrayOf(
      shape({
        ...ChildMapProps,
        id: string,
        isActive: bool,
        xCoord: string,
        yCoord: string,
        hoverLabelOverride: string,
        hoverTitle: string,
        defaultOpenPin: string,
      }),
    ),
  }),
};

const MultiMapApp = (props) => {
  const { pageEditor, parentMapItem, dictionary } = props;

  const breakpoint = 780;
  const initialCoords = { x: 16, y: 40 };
  const mapSrc =
    parentMapItem?.background?.srcSet ||
    "/assets/img/locations-interface-map.svg#locations-interface-background";
  const renderImage = !mapSrc?.includes(".svg#");
  const mapAlt = parentMapItem?.background?.alt || "World Map";
  const activeChildIndex = parentMapItem?.childMapItems?.findIndex(
    (item) => item.isActive,
  );

  const [activeItemIndex, setActiveItemIndex] = useState(activeChildIndex);
  const [wideView, setWideView] = useState(true);
  const [activeTooltipIndex, setActiveTooltipIndex] = useState(-1);
  const [disableTransition, setDisableTransition] = useState(
    activeChildIndex !== -1,
  );

  const activeTooltipRef = useClickAway({
    isOpen: activeTooltipIndex !== -1,
    setOpen: (value) => {
      setActiveTooltipIndex(-1);
    },
  });

  const mapRef = useRef();
  const openingPinRef = useRef();
  const openedChildMapByUserRef = useRef(false); // Track whether the child map was opened by user interaction

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > breakpoint && !wideView) {
        setWideView(true);
      }

      if (window.innerWidth <= breakpoint && wideView) {
        setWideView(false);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [wideView]);

  useEffect(() => {
    initScrolledIntoView(mapRef.current, { rootMargin: "300px 0px 0px 0px" });
  }, [mapRef]);

  const toggleActiveItem = (index, event) => {
    if (pageEditor) {
      return;
    }

    openedChildMapByUserRef.current = true;

    if (index === activeItemIndex) {
      setActiveItemIndex(-1);
    } else {
      openingPinRef.current = event.currentTarget;
      setActiveItemIndex(index);
    }
  };

  const toggleActiveTooltip = (index) => {
    if (pageEditor) {
      return;
    }

    if (index === activeTooltipIndex) {
      setActiveTooltipIndex(-1);
    } else {
      setActiveTooltipIndex(index);
    }
  };

  const handleCloseChildMap = () => {
    setActiveItemIndex(-1);
    setDisableTransition(false);
  };

  // Focus the opening pin button after closing child map
  const handleTransitionEnd = (e) => {
    if (e.target !== e.currentTarget) return; // ignore bubbled events
    if (e.propertyName !== "visibility") return;
    const childMapClosed = activeItemIndex === -1;

    if (childMapClosed) {
      requestAnimationFrame(() => {
        if (openingPinRef.current) {
          openingPinRef.current.focus();
        }
      });
    }
  };

  return (
    <>
      <section
        className="multi-map__container multi-map__parent container"
        onTransitionEnd={handleTransitionEnd}
      >
        {parentMapItem && <MapHeading {...parentMapItem} />}

        {/* PIN COORDINATE INPUT - PAGE EDITOR VIEW */}
        {pageEditor && (
          <div className="multi-map__coordinates js-multi-map-coords">
            <label>
              <span>x:</span>
              <input
                className="js-multi-map-xcoord"
                type="number"
                defaultValue={initialCoords.x}
                name="x"
              />
            </label>
            <button
              className="js-coord-clipboard-x multi-map__copy-btn"
              aria-label="Copy x coordinate to clipboard"
            >
              <Icon iconName="clipboard" />
            </button>
            <label>
              <span>y:</span>
              <input
                className="js-multi-map-ycoord"
                type="number"
                defaultValue={initialCoords.y}
                name="y"
              />
            </label>
            <button
              className="js-coord-clipboard-y multi-map__copy-btn"
              aria-label="Copy x coordinate to clipboard"
            >
              <Icon iconName="clipboard" />
            </button>
          </div>
        )}

        <div className="multi-map__map-container js-multi-map-map" ref={mapRef}>
          {renderImage && (
            <ImageElement
              className="multi-map__map"
              sizes="(min-width: 1170px) 1170px, 100vw"
              {...parentMapItem?.background}
            />
          )}

          {!renderImage && (
            <svg
              focusable="false"
              className="multi-map__map multi-map__map--svg"
              role="img"
              aria-label={mapAlt}
            >
              <use xlinkHref={mapSrc}></use>
            </svg>
          )}

          {pageEditor && (
            <button
              className="multi-map__pin js-multi-map-pin"
              aria-label={"move to pick a spot"}
              draggable={true}
              onClick={() => {}}
              style={{
                top: `${initialCoords.y}%`,
                left: `${initialCoords.x}%`,
                position: "absolute",
              }}
            >
              <span>1</span>
            </button>
          )}

          {!pageEditor && (
            <nav aria-label="our locations" className="multi-map__map-points">
              <ul className="multi-map__items">
                {parentMapItem?.childMapItems &&
                  parentMapItem.childMapItems.map((location, i) => (
                    <li
                      key={i}
                      className={cx("multi-map__item", {
                        "multi-map__mobile-pin-item": !wideView,
                      })}
                    >
                      {wideView && (
                        <div
                          className="multi-map__overlay"
                          style={{
                            top: location.yCoord,
                            left: location.xCoord,
                            position: "absolute",
                          }}
                        >
                          <button
                            className="multi-map__pin"
                            aria-label={
                              location.hoverLabelOverride
                                ? `open map for ${location.hoverLabelOverride}`
                                : `open map for ${location.title}`
                            }
                            aria-haspopup="dialog"
                            aria-expanded={activeItemIndex === i}
                            aria-controls="multi-map-child-dialog"
                            onClick={(event) => toggleActiveItem(i, event)}
                            onMouseEnter={() => toggleActiveTooltip(i)}
                            onFocus={() => toggleActiveTooltip(i)}
                            onBlur={() => toggleActiveTooltip(-1)}
                          >
                            <Icon
                              iconName="add"
                              className="multi-map__pin--icon"
                            />
                          </button>
                          {location?.hoverLabelOverride && (
                            <div
                              id={`parent-pin-${i}`}
                              className={cx("multi-map__tooltip", {
                                active: activeTooltipIndex === i,
                              })}
                              role="tooltip"
                              aria-hidden
                              ref={
                                activeTooltipIndex === i
                                  ? activeTooltipRef
                                  : null
                              }
                            >
                              {location?.hoverLabelOverride}
                            </div>
                          )}
                        </div>
                      )}

                      {!wideView && (
                        <button
                          className="multi-map__pin-mobile"
                          onClick={(e) => toggleActiveItem(i, e)}
                          aria-haspopup="dialog"
                          aria-expanded={activeItemIndex === i}
                          aria-controls="multi-map-child-dialog"
                        >
                          {location?.hoverTitle || location.title}
                          <Icon
                            iconName="arrow-right"
                            className="multi-map__pin-mobile-icon"
                          />
                        </button>
                      )}
                    </li>
                  ))}
              </ul>
            </nav>
          )}
        </div>
      </section>
      {/* CHILD MAP DIALOGS */}

      {/* WIDE VIEW */}
      {/* NEED TO MAP OVER ALL TO USE TRANSITION WRAPPER
      - IF RENDERED PARENT MAP PIN CLICK COUNTS AS A CLICKAWAY AND CLOSES THE INITIAL OPEN DETAIL PIN */}
      {!pageEditor &&
        wideView &&
        parentMapItem.childMapItems.map((child, index) => (
          <TransitionWrapper
            key={index}
            show={activeItemIndex === index && activeItemIndex !== -1}
            baseClass="multi-map__child-dialog-wrap"
            exitClass="exiting"
            disableClass={disableTransition ? "no-transition" : null}
            duration={200} // match CSS keyframe duration
          >
            <ChildMapDialog>
              <MultiMapChildApp
                {...parentMapItem.childMapItems[index]}
                hasParent={true}
                dictionary={dictionary}
                pageEditor={pageEditor}
                handleCloseChildMap={handleCloseChildMap}
                shouldAutoFocus={openedChildMapByUserRef.current}
              />
            </ChildMapDialog>
          </TransitionWrapper>
        ))}

      {/* NARROW VIEW - NEED TO RENDER EVEN IF NOT ACTIVE FOR DRAWER ANIMATION */}
      {!pageEditor && !wideView && (
        <ChildMapDialog isOpen={activeItemIndex !== -1}>
          <MultiMapChildApp
            {...parentMapItem.childMapItems[
              activeItemIndex === -1 ? 0 : activeItemIndex
            ]}
            hasParent={true}
            dictionary={dictionary}
            pageEditor={pageEditor}
            handleCloseChildMap={handleCloseChildMap}
          />
        </ChildMapDialog>
      )}
    </>
  );
};

MultiMapApp.propTypes = propTypes;
export default MultiMapApp;
